import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/shared/lib/supabase'
import { useAuth } from './use-auth'
import type { User } from '@/shared/types'

async function fetchCurrentUser(userId: string): Promise<User | null> {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .single()

  if (error) {
    // User might not have a profile yet (first login)
    if (error.code === 'PGRST116') {
      return null
    }
    throw new Error(error.message)
  }

  return data as User
}

export function useCurrentUser() {
  const { user: authUser, isLoading: isAuthLoading } = useAuth()

  const {
    data: profile,
    isLoading: isProfileLoading,
    error,
  } = useQuery({
    queryKey: ['currentUser', authUser?.id],
    queryFn: () => fetchCurrentUser(authUser!.id),
    enabled: !!authUser?.id,
  })

  const isAdmin = profile?.role === 'admin'
  const isUser = profile?.role === 'user'
  const canCreateProject = isAdmin || isUser // Viewers cannot create projects

  return {
    user: profile,
    authUser,
    isLoading: isAuthLoading || isProfileLoading,
    error,
    isAdmin,
    canCreateProject,
  }
}
